import { useMutation, useQuery } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { UpdateVomDetailsPageQuery } from '~/apollo/generated/v4/graphql';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { VomFormFields } from '~/components/upload/vom/vom-form-fields-v4';
import { useRouteParam } from '~/hooks/routing';
import { useZodForm } from '~/utils/forms';
import { initialVom, vomSchema } from '~/utils/modules/vom-v4';

export const UPDATE_VOM_DETAILS_PAGE = graphql(`
  query UpdateVomDetailsPage($id: ID!) {
    virtualOutcropModelGet(id: $id) {
      ...vomParts
    }
  }
`);

export const UPDATE_VOM = graphql(`
  mutation UpdateVom($id: ID!, $input: VirtualOutcropModelUpdateInput!) {
    virtualOutcropModelUpdate(id: $id, input: $input) {
      result {
        ...vomParts
      }
    }
  }
`);

export default function UpdateVomOverviewPage() {
  return (
    <ApolloProviderV4>
      <UpdateVomOverviewPageLoader />
    </ApolloProviderV4>
  );
}

function UpdateVomOverviewPageLoader() {
  const vomId = useRouteParam('vomId', parseInt);

  const { data, loading } = useQuery(UPDATE_VOM_DETAILS_PAGE, {
    variables: { id: vomId },
  });

  if (loading) return <SpinnerPlaceholder />;
  if (!data?.virtualOutcropModelGet) return <NotFound />;

  return <UpdateVomOverviewPageInner vom={data.virtualOutcropModelGet} />;
}

type Vom = NonNullable<UpdateVomDetailsPageQuery['virtualOutcropModelGet']>;
function UpdateVomOverviewPageInner({ vom }: { vom: Vom }) {
  const [updateVom, { loading, error }] = useMutation(UPDATE_VOM);

  const form = useZodForm({
    schema: vomSchema,
    values: initialVom(vom),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await updateVom({
        variables: {
          id: vom.id,
          input: values,
        },
      });
      toast.success('VOM updated successfully.');
    } catch (err) {
      toast.error('There was a problem updating the VOM.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <VomFormFields />
        <HookFormErrors graphQLError={error} />

        <div className="text-center">
          <Button type="submit" color="primary" loading={loading}>
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
